/**
 * Activity Page - Microsoft 365 Style
 * Login/Logout activity tracking
 */

import React, { useEffect, useState } from 'react';
import { makeStyles, Input, Text, Badge, Avatar, Spinner } from '@fluentui/react-components';
import { SearchRegular, PersonRegular, CalendarRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../components/layout';
import { TablePagination } from '../../components/common';
import {
  ApiClient,
  newToast,
  fullDateTime,
  getErrorMessage,
} from '../../configurations/index.config';
import type { PaginationMeta } from '../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  filters: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  filterItem: {
    minWidth: '200px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  searchBar: {
    marginBottom: '16px',
    maxWidth: '400px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
});

interface ActivityRecord {
  id: string;
  first_name: string;
  last_name: string;
  unique_emp_id: string;
  login_date: string;
  logout_date?: string;
}

export const ActivityFluentUI: React.FC = () => {
  const styles = useStyles();
  const [activities, setActivities] = useState<ActivityRecord[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchActivities();
  }, [currentPage, itemsPerPage, startDate, endDate]);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }

      const response = await ApiClient.get('/login-activity', { params });
      if (response.data.status === 'success') {
        setActivities(response.data.data || []);
        setPagination(response.data.pagination);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Login Activity</Text>
        <Text className={styles.pageSubtitle}>
          {loading ? 'Loading...' : `${pagination?.totalItems || 0} activity records`}
        </Text>
      </div>

      {/* Date Filters */}
      <div className={styles.filters}>
        <div className={styles.filterItem}>
          <Input
            type="date"
            contentBefore={<CalendarRegular />}
            placeholder="Start Date"
            value={startDate}
            onChange={e => {
              setStartDate(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <div className={styles.filterItem}>
          <Input
            type="date"
            contentBefore={<CalendarRegular />}
            placeholder="End Date"
            value={endDate}
            onChange={e => {
              setEndDate(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      {/* Activity Table */}
      <ContentCard noPadding>
        {loading ? (
          <div className={styles.emptyState}>
            <Spinner label="Loading activities..." />
          </div>
        ) : activities.length === 0 ? (
          <div className={styles.emptyState}>
            <PersonRegular style={{ fontSize: '48px', marginBottom: '12px' }} />
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              No activities found
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              Try adjusting your date range filters
            </Text>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.th}>Employee</th>
                <th className={styles.th}>ID</th>
                <th className={styles.th}>Login Time</th>
                <th className={styles.th}>Logout Time</th>
                <th className={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {activities.map(activity => (
                <tr key={activity.id} className={styles.tr}>
                  <td className={styles.td}>
                    <div className={styles.userCell}>
                      <Avatar
                        name={`${activity.first_name} ${activity.last_name}`}
                        color="brand"
                        size={32}
                      />
                      <Text weight="semibold">
                        {activity.first_name} {activity.last_name}
                      </Text>
                    </div>
                  </td>
                  <td className={styles.td}>
                    <Badge appearance="outline">{activity.unique_emp_id}</Badge>
                  </td>
                  <td className={styles.td}>{fullDateTime(activity.login_date)}</td>
                  <td className={styles.td}>
                    {activity.logout_date ? fullDateTime(activity.logout_date) : '—'}
                  </td>
                  <td className={styles.td}>
                    {activity.logout_date ? (
                      <Badge appearance="tint" color="danger">
                        Logged Out
                      </Badge>
                    ) : (
                      <Badge appearance="tint" color="success">
                        Active
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && pagination && activities.length > 0 && (
          <TablePagination
            totalItems={pagination.totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
      </ContentCard>
    </div>
  );
};

export default ActivityFluentUI;
